/**
 * 业务失败码全集（前端侧）。
 *
 * 这张表与后端的**词表文件**一一对应：
 * `backend/src/steam/protocol/reason_codes.json`（由
 * `backend/src/steam/protocol/reason_codes.py` 从 `ERESULT_CODES` ∪
 * `GUARD_BY_TYPE` ∪ 字面量集合生成）。后端多发一个码而这里没跟上，
 * 契约测试会红：`frontend/src/shared/domain/reasonVocabulary.test.ts`。
 *
 * 例外是**已退役 / 改名过的旧值**（`guard_code_expired`、
 * `guard_2fa_confirm`、`no_proxy_available`）：后端不再发射，但历史值
 * 回读时不该退化成原始码 ⇒ 表里留着（契约测试的 `FRONTEND_ONLY`）。
 *
 * 后端有**两套大小写**拼法（同一码的两种形状，不是两套码）：
 * - verdict 路径给业务码（小写 snake）——`code_of_eresult`；
 * - 异常分类路径给官方 EResult 名（大写）——`classify.py`。
 * 查表前统一小写即可（先例：`steamOrders/SteamHistoryPage.tsx` 的
 * `code.trim().toLowerCase()`）。
 */
export const PLATFORM_REASONS = [
  'account_banned',
  'account_disabled',
  'account_limited',
  'account_locked',
  'busy',
  'captcha_required',
  'credentials_empty',
  'credentials_invalid',
  'duplicate_request',
  // 汇率刷新两档失败（`fx.refresh_failed` 帧的 `failure_code`）——2026-10-23 起
  // 从点号形折成平台的统一 snake 形状（ADR 0011 §5）。
  'fx_refresh_db_write_failed',
  'fx_refresh_http_failed',
  'guard_2fa_code',
  'guard_code_invalid',
  'guard_device_confirm',
  'guard_email_code',
  'guard_email_confirm',
  'invalid_argument',
  'invalid_response',
  'ip_banned',
  'login_throttled',
  'network_unreachable',
  'no_credentials',
  'not_logged_on',
  'pending',
  'rate_limited',
  'settle_refused',
  'ssrf_blocked',
  'transport_timeout',
  'unavailable',
  // Steam 返回未识别的错误码（历史 UNKNOWN 行兜底）——原样展示。
  'unknown',
  // ── 历史别名：落过库的旧值，改名窗口内产生 ──
  // guard_code_expired（2026-10-20 改名为 guard_code_invalid 之前落库）。
  'guard_code_expired',
  // guard_2fa_confirm（guard 4 的旧名——旧名把「App 确认」与「2FA 确认」
  // 混成一个语义，会误导排障；4 号的正名是 guard_device_confirm）。
  'guard_2fa_confirm',
  // no_proxy_available（**已退役**的码，2026-10-22）：Steam 历史面放开直连档
  // 后后端不再发射它，已从后端词表摘除；但历史响应里存过 ⇒ 文案留着。
  'no_proxy_available',
  // transport_error（**已退役**的码，2026-10-24，ADR 0014）：历史端点三路
  // 收进 classify_with_cause 后后端不再发射；但历史行/响应里存过 ⇒ 文案留着。
  'transport_error',
] as const;
export type PlatformReason = (typeof PLATFORM_REASONS)[number];
export const PLATFORM_REASON_KEY: Record<PlatformReason, string> = {
  account_banned: 'reasonCodes.account_banned',
  account_disabled: 'reasonCodes.account_disabled',
  account_limited: 'reasonCodes.account_limited',
  account_locked: 'reasonCodes.account_locked',
  busy: 'reasonCodes.busy',
  captcha_required: 'reasonCodes.captcha_required',
  credentials_empty: 'reasonCodes.credentials_empty',
  credentials_invalid: 'reasonCodes.credentials_invalid',
  duplicate_request: 'reasonCodes.duplicate_request',
  // 汇率刷新两档（码与 i18n 键同名——平台词表的形状就是 snake）。
  fx_refresh_db_write_failed: 'reasonCodes.fx_refresh_db_write_failed',
  fx_refresh_http_failed: 'reasonCodes.fx_refresh_http_failed',
  guard_2fa_code: 'reasonCodes.guard_2fa_code',
  guard_code_invalid: 'reasonCodes.guard_code_invalid',
  guard_device_confirm: 'reasonCodes.guard_device_confirm',
  guard_email_code: 'reasonCodes.guard_email_code',
  guard_email_confirm: 'reasonCodes.guard_email_confirm',
  invalid_argument: 'reasonCodes.invalid_argument',
  invalid_response: 'reasonCodes.invalid_response',
  ip_banned: 'reasonCodes.ip_banned',
  login_throttled: 'reasonCodes.login_throttled',
  network_unreachable: 'reasonCodes.network_unreachable',
  no_credentials: 'reasonCodes.no_credentials',
  not_logged_on: 'reasonCodes.not_logged_on',
  pending: 'reasonCodes.pending',
  rate_limited: 'reasonCodes.rate_limited',
  settle_refused: 'reasonCodes.settle_refused',
  ssrf_blocked: 'reasonCodes.ssrf_blocked',
  transport_timeout: 'reasonCodes.transport_timeout',
  unavailable: 'reasonCodes.unavailable',
  unknown: 'reasonCodes.unknown',
  // 历史别名指向现役文案（不留孤儿 locale 键）。
  guard_code_expired: 'reasonCodes.guard_code_invalid',
  guard_2fa_confirm: 'reasonCodes.guard_device_confirm',
  // 已退役的码（后端不再发射，历史值回读仍要有人话）。
  no_proxy_available: 'reasonCodes.no_proxy_available',
  transport_error: 'reasonCodes.transport_error',
};
export function platformReasonKey(reason: string): string | null {
  // 后端两套大小写拼法（业务码小写 / 官方 EResult 名大写）在此收口。
  const key = reason.trim().toLowerCase();
  return PLATFORM_REASON_KEY[key as PlatformReason] ?? null;
}
